import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Form, Table, Offcanvas } from "react-bootstrap";
import {
  terminateSims,
  validateSimsForAction,
} from "../../../../redux/slices/sims";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { Formik } from "formik";
import * as Yup from "yup";
import NotyfContext from "../../../../contexts/NotyfContext";
import Loader from "../../../../components/spinners/Loader";
import useWindowSize from "../../../../hooks/useWindowSize";
import useDeviceType from "../../../../hooks/useDeviceType";

const TerminateSimModal = ({
  sims,
  openTerminateModal,
  setOpenTerminateModal,
  clearSelectedRows,
}) => {
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const notyf = useContext(NotyfContext);
  const [internalShow, setInternalShow] = useState(openTerminateModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [fileInputKey, setFileInputKey] = React.useState(Date.now());

  useEffect(() => {
    if (openTerminateModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [openTerminateModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setOpenTerminateModal(false);
    }, 300);
  };

  const initialValues = {
    targetState: "",
    sims: sims.length > 0 ? sims : [],
    file: null,
  };

  const validation = Yup.object().shape({
    targetState: Yup.string().required("Required"),
    sims: Yup.array().required("Required"),
    file: Yup.mixed().when("$requireFile", {
      is: true,
      then: Yup.mixed()
        .required("File is required")
        .test("fileType", "Only CSV or XLSX files are accepted", (value) => {
          if (!value) return false;
          const allowedTypes = [
            "text/csv",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
          ];
          return allowedTypes.includes(value.type);
        }),
      otherwise: Yup.mixed().nullable().notRequired(),
    }),
  });

  if (isDesktop) {
    return (
      <Modal
        show={openTerminateModal}
        onHide={() => {
          setOpenTerminateModal(false);
          setCurrentStep(1);
        }}
        backdrop="static"
        centered
        size="lg"
        scrollable
      >
        <Modal.Header>
          <Modal.Title>Terminate SIMs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            context={{ requireFile: sims.length === 0 }}
            onSubmit={async (values, { setValues, setStatus }) => {
              await dispatch(
                terminateSims({
                  sims: values.sims.map((sim) => sim.imsi),
                  targetState: values.targetState,
                })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs terminated successfully.",
                    dismissible: true,
                    ripple: true,
                    duration: 5000,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    dismissible: true,
                    ripple: true,
                    duration: 5000,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldTouched,
              validateForm,
              status,
              setValues,
              setFieldValue,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && values.sims.length === 0 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Form.Label>Upload File</Form.Label>

                        <div className="custom-file-upload-wrapper">
                          <label
                            htmlFor="file-upload"
                            className="custom-file-upload-btn"
                            style={{
                              "--dynamic-font-color":
                                whitelabel?.fontColor ?? "#fff",
                              "--dynamic-bg-color":
                                whitelabel?.backgroundColor ?? "#0E1A2B",
                              border: "none",
                            }}
                          >
                            Choose File
                          </label>
                          <input
                            key={fileInputKey}
                            id="file-upload"
                            type="file"
                            name="file"
                            accept=".csv,.xlsx"
                            style={{ display: "none" }}
                            onChange={async (event) => {
                              setFieldValue(
                                "file",
                                event.currentTarget.files[0]
                              );
                              await validateSimsForAction({
                                file: event.currentTarget.files[0],
                                action: "terminate",
                              })
                                .then((response) => {
                                  notyf.open({
                                    type: "success",
                                    message: "Sim file validated successfully",
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("sims", response);
                                })
                                .catch((error) => {
                                  notyf.open({
                                    type: "error",
                                    message: error,
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("file", null);
                                  setFileInputKey(new Date());
                                });
                            }}
                            onBlur={() => setFieldTouched("file", true)}
                          />
                          {values.file && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Selected: {values.file.name}
                              </small>
                            </div>
                          )}
                          {touched.file && errors.file && (
                            <div className="text-danger mt-1">
                              {errors.file}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </React.Fragment>
                  )}
                  {currentStep === 1 && values.sims.length > 0 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Table responsive hover>
                          <thead>
                            <tr>
                              <th>IMSI</th>
                              <th>ICCID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {values.sims.map((sim) => (
                              <tr key={sim.imsi}>
                                <td>{sim.imsi}</td>
                                <td>{sim.iccid}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Target State</Form.Label>
                        <Form.Select
                          name="targetState"
                          value={values.targetState}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.targetState && errors.targetState}
                        >
                          <option value="">Select Target State</option>
                          <option value="OnStock">On Stock</option>
                          <option value="Deleted">Terminated</option>
                        </Form.Select>
                        {touched.targetState && errors.targetState && (
                          <Form.Control.Feedback type="invalid">
                            {errors.targetState}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      <FaCheckCircle size={120} color="#4BBF73" />
                      <h3>Success</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      <FaExclamationCircle size={120} color="#FF0000" />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <Modal.Footer>
                    {currentStep === 1 && (
                      <React.Fragment>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                          onClick={() => {
                            setCurrentStep(1);
                            setValues(initialValues);
                            setOpenTerminateModal(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                          onClick={nextStep}
                        >
                          Submit
                        </Button>
                      </React.Fragment>
                    )}
                    {currentStep === 3 && (
                      <Button
                        variant="primary"
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                        }}
                        onClick={() => {
                          setCurrentStep(1);
                          setValues(initialValues);
                          setOpenTerminateModal(false);
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        placement="bottom"
        show={internalShow}
        onHide={handleHide}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>Terminate SIMs</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues, setStatus }) => {
              await dispatch(
                terminateSims({
                  sims: values.sims.map((sim) => sim.imsi),
                  targetState: values.targetState,
                })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs terminated successfully.",
                    dismissible: true,
                    ripple: true,
                    duration: 5000,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    dismissible: true,
                    ripple: true,
                    duration: 5000,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldTouched,
              validateForm,
              status,
              setValues,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Table responsive hover>
                          <thead>
                            <tr>
                              <th>IMSI</th>
                              <th>ICCID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {values.sims.map((sim) => (
                              <tr key={sim.imsi}>
                                <td>{sim.imsi}</td>
                                <td>{sim.iccid}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Target State</Form.Label>
                        <Form.Select
                          name="targetState"
                          value={values.targetState}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.targetState && errors.targetState}
                        >
                          <option value="">Select Target State</option>
                          <option value="OnStock">On Stock</option>
                          <option value="Deleted">Terminated</option>
                        </Form.Select>
                        {touched.targetState && errors.targetState && (
                          <Form.Control.Feedback type="invalid">
                            {errors.targetState}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      <FaCheckCircle size={120} color="#4BBF73" />
                      <h3>Success</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      <FaExclamationCircle size={120} color="#FF0000" />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <div className="d-flex flex-column align-items-center mt-3">
                    {currentStep === 1 && (
                      <React.Fragment>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          className="mb-3"
                          onClick={nextStep}
                        >
                          Submit
                        </Button>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          onClick={() => {
                            setCurrentStep(1);
                            setValues(initialValues);
                            handleHide();
                          }}
                        >
                          Cancel
                        </Button>
                      </React.Fragment>
                    )}
                    {currentStep === 3 && (
                      <Button
                        variant="primary"
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                          padding: "10px",
                          width: "100%",
                        }}
                        onClick={() => {
                          setCurrentStep(1);
                          setValues(initialValues);
                          handleHide();
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default TerminateSimModal;
